import { useEffect, useState } from 'react';
import { describeActivity, listAuditEvents, type AuditEventDetail } from '../../api/activity';
import { DashboardSkeleton, Drawer, ErrorNote, EmptyState, SectionCard, StatusBadge, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconArchive, IconBuilding, IconLayers, IconShield, IconTrendUp, IconWallet } from '../../components/icons';

interface Capability {
  key: string;
  label: string;
  icon: (props: { size?: number }) => JSX.Element;
  status: 'not_connected' | 'foundation';
  summary: string;
  detail: string;
}

/**
 * Platform Owner dashboard — truthful, not illustrative. Cross-tenant
 * billing/usage has no authoritative source in this vertical slice:
 * `tenants`/`subscriptions` use the identical tenant_id =
 * app_current_tenant_id() RLS isolation as every other table, so no role
 * — including Platform Owner — can structurally read across tenants
 * today without a new, deliberately designed access mechanism. Rather
 * than fabricate tenant counts, MRR, or package distributions, the
 * primary panel here is a REAL one: the tenant's own audit_events feed
 * (same RLS-scoped table the Auditor dashboard and topbar bell read) —
 * genuine system activity a platform administrator would want to see,
 * not a stand-in for cross-tenant data. Unavailable platform-scale
 * capabilities are grouped into one honest "not connected" list rather
 * than scattered fake widgets.
 */
const CAPABILITIES: Capability[] = [
  {
    key: 'cross-tenant',
    label: 'Cross-tenant Analytics',
    icon: IconBuilding,
    status: 'not_connected',
    summary: 'Isolasi RLS per-tenant',
    detail:
      'Tabel tenants menggunakan isolasi RLS per-tenant yang sama seperti semua tabel lain (tenant_id = app_current_tenant_id()). Tidak ada peran — termasuk Platform Owner — yang bisa membaca lintas-tenant hari ini tanpa mekanisme akses baru yang sengaja dirancang (mis. fungsi SECURITY DEFINER khusus dengan audit trail).',
  },
  {
    key: 'billing',
    label: 'Subscription & Billing',
    icon: IconWallet,
    status: 'not_connected',
    summary: 'Modul belum dibangun',
    detail: 'Belum ada modul billing/subscription yang diimplementasikan di vertical slice ini — tidak ada MRR, paket, atau status langganan nyata untuk ditampilkan.',
  },
  {
    key: 'telemetry',
    label: 'Platform Telemetry',
    icon: IconTrendUp,
    status: 'not_connected',
    summary: 'Endpoint belum terhubung',
    detail: 'Uptime, latency, dan error rate akan bersumber dari health-check/monitoring nyata pada integrasi produksi. Belum ada endpoint telemetry yang terhubung ke build ini.',
  },
  {
    key: 'integration',
    label: 'Integration Status',
    icon: IconLayers,
    status: 'foundation',
    summary: 'Supabase Auth · Postgres · PostgREST',
    detail: 'Autentikasi (Supabase Auth), database (Postgres 17), dan API data (PostgREST + RPC) aktif dan terhubung — ini fondasi platform yang sudah nyata, bukan ilustrasi.',
  },
  {
    key: 'security',
    label: 'Security Posture',
    icon: IconShield,
    status: 'foundation',
    summary: 'RLS + RBAC catalog: locked',
    detail:
      'Row-Level Security aktif di seluruh tabel operasional dengan isolasi per-tenant dan per-cabang. Katalog roles/permissions/role_permissions terkunci dari akses langsung anon/authenticated (migrasi 0037) — hanya bisa dibaca lewat fungsi SECURITY DEFINER yang diperiksa.',
  },
  {
    key: 'config',
    label: 'System Configuration',
    icon: IconArchive,
    status: 'foundation',
    summary: 'Branch & role catalog',
    detail: 'Pengaturan cabang dan peran dikelola melalui Master Data dan RBAC — lihat menu Suppliers/Items untuk data operasional tenant Anda saat ini.',
  },
];

export default function PlatformOwnerDashboard() {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [events, setEvents] = useState<AuditEventDetail[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const open = CAPABILITIES.find((c) => c.key === openKey) ?? null;
  const notConnected = CAPABILITIES.filter((c) => c.status === 'not_connected');
  const foundation = CAPABILITIES.filter((c) => c.status === 'foundation');

  useEffect(() => {
    let cancelled = false;
    listAuditEvents(30)
      .then((rows) => !cancelled && setEvents(rows))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <p style={{ fontSize: 12.5, color: 'var(--alex-text-faint)', margin: '0 0 16px', maxWidth: 680, lineHeight: 1.5 }}>
        Analitik lintas-tenant belum tersedia secara arsitektural. Panel di bawah menampilkan aktivitas nyata tenant Anda saat ini, plus status fondasi platform.
      </p>

      <div className="alex-grid-8-4">
        <SectionCard title="Aktivitas Sistem Terbaru">
          {loading && <DashboardSkeleton />}
          {!loading && error && <ErrorNote text={error} />}
          {!loading && !error && (!events || events.length === 0) && <EmptyState text="Belum ada aktivitas sistem tercatat." variant="table" />}
          {!loading && !error && events && events.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Waktu</th>
                  <th style={thStyle}>Kejadian</th>
                  <th style={thStyle}>Aksi</th>
                  <th style={thStyle}>Referensi</th>
                </tr>
              </thead>
              <tbody>
                {events.slice(0, 12).map((e) => (
                  <tr key={e.event_id}>
                    <td style={tdStyle}>{new Date(e.occurred_at).toLocaleString('id-ID')}</td>
                    <td style={tdStyle}>{describeActivity(e)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={e.action} />
                    </td>
                    <td style={tdStyle}>{e.reference ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Fondasi Platform">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {foundation.map((cap) => (
                <button
                  key={cap.key}
                  onClick={() => setOpenKey(cap.key)}
                  className="alex-row-clickable"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: 'none', background: 'none', padding: '8px 6px', borderRadius: 'var(--alex-control-radius)', width: '100%', textAlign: 'left' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <cap.icon size={16} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)' }}>{cap.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--alex-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cap.summary}</div>
                    </div>
                  </div>
                  <StatusBadge status="TERHUBUNG" />
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Belum Terhubung">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {notConnected.map((cap) => (
                <button
                  key={cap.key}
                  onClick={() => setOpenKey(cap.key)}
                  className="alex-row-clickable"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: 'none', background: 'none', padding: '8px 6px', borderRadius: 'var(--alex-control-radius)', width: '100%', textAlign: 'left' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <cap.icon size={16} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)' }}>{cap.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--alex-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cap.summary}</div>
                    </div>
                  </div>
                  <StatusBadge status="BELUM TERHUBUNG" />
                </button>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      <Drawer open={!!open} onClose={() => setOpenKey(null)} title={open?.label ?? ''}>
        {open && <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', lineHeight: 1.55, margin: 0 }}>{open.detail}</p>}
      </Drawer>
    </div>
  );
}
